import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsisH } from '@fortawesome/pro-solid-svg-icons/faEllipsisH';

import React, { useCallback, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { noop } from 'ui-components/default-props/noop';
import { ActiveClientProptype, PendingClientProptype, InactiveClientProptype } from './prop-types';
import { FormattedMessage } from 'react-intl';
import { DropdownMenu } from 'ui-components/components/dropdown-menu';
import { DropdownMenuToggler } from 'ui-components/components/dropdown-menu-toggler';
import { DropdownMenuItem } from 'ui-components/components/dropdown-menu-item';
import { useModal } from '../../../hooks/use-modal';
import CancelPendingClientModal from './cancel-pending-client-modal';
import { DeactivateClientModal } from './deactivate-client-modal';
import ResendInviteModal from './invite-client-modal/resend-invite-modal';
import messages from './messages';
import style from './style.module.less';
import { ClientStatus } from './constants';
import { emptyArray } from 'ui-components/default-props/empty';

const MODALS = {
  cancelPendingClient: 'cancelPendingClient',
  resendPendingInvite: 'resendPendingInvite',
  deactivateClient: 'deactivateClient',
};

const ClientGridActionButtons = ({ client, reloadGrid = noop, existingClients = emptyArray }) => {
  const { close, isOpen, open } = useModal();
  const [selectedModal, setSelectedModal] = useState();

  const openCancelPendingClientModal = useCallback(() => {
    setSelectedModal(MODALS.cancelPendingClient);
    open();
  }, [open]);

  const openResendPendingInviteModal = useCallback(() => {
    setSelectedModal(MODALS.resendPendingInvite);
    open();
  }, [open]);

  const openDeactivateClientModal = useCallback(() => {
    setSelectedModal(MODALS.deactivateClient);
    open();
  }, [open]);

  const pendingInvite = useMemo(
    () => ({
      recipientName: client.clientName,
      recipientEmail: client.clientEmail,
      externalInviteId: client.externalInviteId,
    }),
    [client.clientName, client.clientEmail, client.externalInviteId],
  );

  return (
    <>
      <DropdownMenu menuPosition='bottom-left'>
        <DropdownMenuToggler className={style['menu-toggle']} hideChevron openClassName={style['open-menu-toggle']} theme='link'>
          <div className={style['menu-toggle-contents']}>
            <FontAwesomeIcon icon={faEllipsisH} />
          </div>
        </DropdownMenuToggler>
        {client.status === ClientStatus.PENDING && (
          <DropdownMenuItem onClick={openResendPendingInviteModal}>
            <FormattedMessage {...messages.resendPendingInvite} />
          </DropdownMenuItem>
        )}
        {client.status === ClientStatus.PENDING && (
          <DropdownMenuItem onClick={openCancelPendingClientModal} theme='danger-link'>
            <FormattedMessage {...messages.cancelPendingClientButton} />
          </DropdownMenuItem>
        )}
        {client.status === ClientStatus.ACTIVE && (
          <DropdownMenuItem onClick={openDeactivateClientModal} theme='danger-link'>
            <FormattedMessage {...messages.deactivateClientButton} />
          </DropdownMenuItem>
        )}
      </DropdownMenu>

      {isOpen && selectedModal === MODALS.resendPendingInvite && (
        <ResendInviteModal existingClients={existingClients} onAddSuccess={reloadGrid} onClose={close} pendingInvite={pendingInvite} />
      )}
      {isOpen && selectedModal === MODALS.cancelPendingClient && (
        <CancelPendingClientModal client={client} onClose={close} reloadGrid={reloadGrid} />
      )}
      {isOpen && selectedModal === MODALS.deactivateClient && (
        <DeactivateClientModal client={client} onClose={close} reloadGrid={reloadGrid} />
      )}
    </>
  );
};

ClientGridActionButtons.propTypes = {
  client: PropTypes.oneOfType([ActiveClientProptype, PendingClientProptype, InactiveClientProptype]).isRequired,
  existingClients: PropTypes.arrayOf(
    PropTypes.shape({
      clientName: PropTypes.string,
      clientEmail: PropTypes.string,
      status: PropTypes.oneOf([ClientStatus.ACTIVE, ClientStatus.PENDING]),
      id: PropTypes.string,
    }),
  ),
  reloadGrid: PropTypes.func,
};

export default ClientGridActionButtons;
